/* eslint-disable no-unused-expressions */
/* eslint-disable react/jsx-no-constructed-context-values */
import { TCartChanges } from '@cannect/services/resources/cart/types'
import { IChildTypes } from '@cannect/types/ChildrenTypes'
import { useLocalStorage } from 'hooks/useLocalStorage'
import useQueryString from 'hooks/useQueryString'
import useSignedUser from 'hooks/useSignedUser'
import { noop } from 'lodash'
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import api from 'services/api'
import { clearSessionCartId, getSessionCartId, initSessionCartId, isSessionCartResolved } from 'utils/anonymousCart'

interface CartCheckoutContextValues {
  addToCart: (
    id: number,
    quantity?: number,
    class_id?: number,
    brand_id?: number,
    type?: string,
    is_cannabis?: boolean
  ) => void
  removeCart: (id: number) => void
  incrementOrDecrementCart: (id: number, quantity: number) => void
  clearCart: () => void
  lastOrderCreated: any
  newLastOrder: (val: any) => void
  getLastOrder: (loading?: boolean) => void
  getPublicCart: ({ cart_id }: { cart_id: string }) => void
  loading: boolean
  setLoading: (val: boolean) => void
  paymentRetry: boolean
  setPaymentRetry: (val: boolean) => void
  setCartId: (val: any) => void
  cartId: any
  recipesDocuments: any
  setRecipeDocuments: any
  updateCartCoupon: (coupon: any, item: any, shippingDiscount?: number) => void
  loadingCupon: boolean
  isUpdate: boolean
  updateCart: (items?: any[]) => void
  handlePatientIsUser: () => void
  patientIsUser: boolean
  updateCartRecipes: (recipes: any) => void
  statusPaid: any
  addToCartFlavor: (idRemove: number, id: number, quantity?: number) => void
  setOrderId: (val: any) => void
  clearOrderData: () => void
  AddToCartByFlavor: (items: any[]) => void
  setOrderData: (val: any) => void
  cartLoading: boolean
  coupon: any
  removeCoupon: () => void
  lastOrderPaid: any
  getLastOrderPaid: (userId: number) => void
  showCartModifiedModal: boolean
  setShowCartModifiedModal: (val: boolean) => void
  cartChanges: TCartChanges | null
  deliveryCep: string
  setDeliveryCep: (val: string) => void
  validateDeliveryCep: () => Promise<boolean>
  validatingDeliveryCep: boolean
  anonOrderData: any
  getAnonymousCart: () => Promise<void>
  updateAnonItem: (itemId: number, quantity: number) => Promise<void>
  removeAnonItem: (itemId: number) => Promise<void>
  anonShippingData: AnonShippingData | null
  validateAnonDeliveryCep: (opts?: { silent?: boolean }) => Promise<boolean>
}

interface DeliveryEstimateSnapshot {
  orderId: number | null
  cep: string
  estimated_delivery_business_days: number | null
  estimated_delivery_range_start: string | null
  estimated_delivery_range_end: string | null
  estimated_delivery_incomplete: boolean | null
}

interface DeliveryEstimateSplit {
  business_days: number | null
  range_start: string | null
  range_end: string | null
}

interface AnonShippingData {
  shipping: number
  national_freight: number
  estimated_delivery_business_days: number | null
  estimated_delivery_range_start: string | null
  estimated_delivery_range_end: string | null
  estimated_delivery_incomplete: boolean
  estimated_delivery_pill: DeliveryEstimateSplit | null
  estimated_delivery_others: DeliveryEstimateSplit | null
  shipping_restriction: {
    requires_cep: boolean
    carrier: string
    covered: boolean
    blocked_products: { product_id: number; name: string }[]
  } | null
}

const defaultPrepareCartContextValues = {
  cartItems: [],
  addToCart: noop,
  removeCart: noop,
  clearCart: noop,
  incrementOrDecrementCart: noop,
  setCartItems: noop,
  lastOrderCreated: {},
  newLastOrder: noop,
  getLastOrder: noop,
  getPublicCart: noop,
  loading: false,
  setLoading: noop,
  recipesDocuments: {},
  setRecipeDocuments: noop,
  getLastOrderOrUpdatedCart: noop,
  updateCartCoupon: noop,
  loadingCupon: false,
  isUpdate: false,
  cartLoading: false,
  updateCart: noop,
  patientIsUser: false,
  handlePatientIsUser: noop,
  updateCartRecipes: noop,
  statusPaid: {},
  addToCartFlavor: noop,
  setOrderId: noop,
  clearOrderData: noop,
  AddToCartByFlavor: noop,
  setPaymentRetry: noop,
  paymentRetry: false,
  setCartId: noop,
  cartId: null,
  setOrderData: noop,
  coupon: null,
  removeCoupon: noop,
  lastOrderPaid: null,
  getLastOrderPaid: noop,
  showCartModifiedModal: false,
  setShowCartModifiedModal: noop,
  cartChanges: null,
  deliveryCep: '',
  setDeliveryCep: noop,
  validateDeliveryCep: async () => false,
  validatingDeliveryCep: false,
  anonOrderData: { items: [] },
  getAnonymousCart: async () => {},
  updateAnonItem: async () => {},
  removeAnonItem: async () => {},
  anonShippingData: null,
  validateAnonDeliveryCep: async () => false
}

const PrepareCartContext = createContext<CartCheckoutContextValues>(defaultPrepareCartContextValues)

const normalizeCep = (value: string) => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 8)
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits
}

const getDeliveryEstimateSnapshotFromOrder = (order: any, cep: string): DeliveryEstimateSnapshot | null => {
  if (!order?.id) return null

  return {
    orderId: Number(order.id),
    cep: normalizeCep(cep || order?.cep || ''),
    estimated_delivery_business_days: order?.estimated_delivery_business_days ?? null,
    estimated_delivery_range_start: order?.estimated_delivery_range_start ?? null,
    estimated_delivery_range_end: order?.estimated_delivery_range_end ?? null,
    estimated_delivery_incomplete: order?.estimated_delivery_incomplete ?? null
  }
}

const hasOrderDeliveryEstimate = (order: any) => {
  return Boolean(
    order?.estimated_delivery_range_start ||
    order?.estimated_delivery_range_end ||
    (order?.estimated_delivery_business_days !== null && order?.estimated_delivery_business_days !== undefined)
  )
}

const getCartItemsSignature = (items: any[]) => {
  if (!Array.isArray(items) || items.length === 0) return ''

  return items
    .map((item: any) => {
      const productId = item?.product_id || item?.product?.id || item?.id || 'unknown'
      const quantity = item?.quantity || 0
      return `${productId}:${quantity}`
    })
    .sort()
    .join('|')
}

const applyDeliveryEstimateSnapshotToOrder = (order: any, snapshot: DeliveryEstimateSnapshot | null) => {
  if (!order?.id || !snapshot?.orderId) return order
  if (Number(order.id) !== Number(snapshot.orderId)) return order

  const currentCep = normalizeCep(order?.cep || '')
  const snapshotCep = normalizeCep(snapshot.cep || '')

  if (snapshotCep && currentCep && snapshotCep !== currentCep) {
    return order
  }

  const hasEstimate =
    order?.estimated_delivery_range_start ||
    order?.estimated_delivery_range_end ||
    (order?.estimated_delivery_business_days !== null && order?.estimated_delivery_business_days !== undefined)

  if (hasEstimate) return order

  return {
    ...order,
    estimated_delivery_business_days: snapshot.estimated_delivery_business_days,
    estimated_delivery_range_start: snapshot.estimated_delivery_range_start,
    estimated_delivery_range_end: snapshot.estimated_delivery_range_end,
    estimated_delivery_incomplete: snapshot.estimated_delivery_incomplete
  }
}

function PrepareCartProvider({ children }: IChildTypes) {
  const [orderData, setOrderData] = useState<any>({ items: [], recipes: [] })
  const [recipesDocuments, setRecipeDocuments] = useState<any>([])
  const [loading, setLoading] = useState(false)
  const [isUpdate, setIsUpdate] = useState(false)
  const [loadingCupon, setLoadingCupon] = useState(false)
  const [paymentRetry, setPaymentRetry] = useState(false)
  const [stateOrderId, setStateOrderId] = useState(null)
  const [patientIsUser, setPatientIsUser] = useState(true)
  const [statusPaid, setStatusPaid] = useState<any>({})
  const [cartId, setCartId] = useState(null)
  const [validatedStatus, setValidatedStatus] = useState(false)
  const [cartLoading, setCartLoading] = useState(false)
  const [coupon, setCoupon] = useState<any>(null)
  const [anonOrderData, setAnonOrderData] = useState<any>({ items: [] })
  const [anonShippingData, setAnonShippingData] = useState<AnonShippingData | null>(null)
  const anonItemsSignatureRef = useRef('')
  const { filters } = useQueryString()
  const withFilter = filters?.cart_id
  const [orderId, setOrderId] = useLocalStorage('@CANNECT:ORDER_CHECKOUT_CART_ID', null)
  const [lastOrderPaid, setLastOrderPaid] = useState<any>({})
  const [deliveryCep, setDeliveryCepState] = useLocalStorage<string>('@CANNECT:DELIVERY_CEP', '')
  const [deliveryEstimateSnapshot, setDeliveryEstimateSnapshot] = useLocalStorage<DeliveryEstimateSnapshot | null>(
    '@CANNECT:DELIVERY_ESTIMATE',
    null
  )
  const [validatingDeliveryCep, setValidatingDeliveryCep] = useState(false)
  const cartItemsRef = useRef(orderData?.items || [])
  const [showCartModifiedModal, setShowCartModifiedModal] = useState(false)
  const [cartChanges, setCartChanges] = useState<TCartChanges | null>(null)
  const lastPersonIdRef = useRef<number | null>(null)
  const lastUserIdRef = useRef<number | null>(null)
  const lastAutoEstimatedKeyRef = useRef('')
  const finalizedOrderIdRef = useRef<number | null>(null)
  const seededCepFromOrderRef = useRef<number | null>(null)
  const { signedUser } = useSignedUser()
  const history = useHistory()
  const isPublicUrl = withFilter ? 'order_cart_public' : 'order_cart'

  const setDeliveryCep = (val: string) => {
    const masked = normalizeCep(val)
    const snapshotCep = normalizeCep(deliveryEstimateSnapshot?.cep || '')

    if (snapshotCep && snapshotCep !== masked) {
      setDeliveryEstimateSnapshot(null)
    }

    setDeliveryCepState(masked)
  }

  useEffect(() => {
    if (!withFilter) return
    lastAutoEstimatedKeyRef.current = ''
    seededCepFromOrderRef.current = null
    setDeliveryCep('')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset ao abrir/trocar carrinho-wa; setDeliveryCep não é estável
  }, [withFilter])

  const syncDeliveryEstimate = async ({ silent = false } = {}): Promise<boolean> => {
    const cepDigits = (deliveryCep || '').replace(/\D/g, '')
    if (cepDigits.length !== 8) {
      if (!silent) toast.error('Informe um CEP válido')
      return false
    }

    if (!orderData?.id) {
      // Sem order (usuário não autenticado): apenas persiste o CEP localmente
      if (!silent) toast.success('CEP salvo com sucesso')
      return true
    }

    setValidatingDeliveryCep(true)
    try {
      const items = (orderData?.items || []).map((item: any) => {
        const product = item?.product
        const payload: any = {
          product_id: item?.product_id || product?.id || item?.id,
          quantity: item?.quantity || 1,
          class_id: item?.class_id ?? product?.class_id,
          brand_id: item?.brand_id ?? product?.brand_id,
          type: item?.type ?? product?.type
        }
        const classId = item?.class_id ?? product?.class_id
        const brandId = item?.brand_id ?? product?.brand_id
        if (classId != null) payload.class_id = classId
        if (brandId != null) payload.brand_id = brandId
        return payload
      })

      const isReshipping = orderData?.origin === 'REENVIO'

      const payload: any = {
        ...(isReshipping ? {} : { items }),
        is_discrete_shipping: !!orderData?.is_discrete_shipping,
        shippingAddress: {
          cep: deliveryCep,
          is_discrete_shipping: !!orderData?.is_discrete_shipping
        }
      }

      if (orderData?.coupon?.name) {
        payload.coupon = { name: orderData.coupon.name }
      }

      const { data } = await api.put(`/${isPublicUrl}/${orderData.id}`, payload)
      if (data?.order) {
        setOrderData(data.order)
        setPatientIsUser(data?.patientIsUser)
        setDeliveryEstimateSnapshot(getDeliveryEstimateSnapshotFromOrder(data.order, deliveryCep))
      }

      if (!silent) {
        const restriction = data?.order?.shipping_restriction ?? null
        if (restriction?.covered !== false) {
          toast.success('CEP validado com sucesso')
        }
      }
      return true
    } catch (e: any) {
      console.error(e)
      const status = e?.response?.status
      // Sem sessão (401/403) repetir não resolve: a rota autenticada seguiria recusando, igual ao pedido finalizado.
      if (
        status === 401 ||
        status === 403 ||
        (status === 400 && e?.response?.data?.error === 'Pedido já foi finalizado')
      ) {
        finalizedOrderIdRef.current = orderData?.id ?? null
      }
      const message = e?.response?.data?.message || 'Erro ao validar CEP'
      if (!silent) {
        toast.error(message)
      }
      return false
    } finally {
      setValidatingDeliveryCep(false)
    }
  }

  const validateDeliveryCep = async (): Promise<boolean> => {
    return syncDeliveryEstimate({ silent: false })
  }

  useEffect(() => {
    const currentUserId = signedUser?.id
    const LAST_USER_ID_KEY = '@CANNECT:LAST_USER_ID'
    const storedLastUserId = localStorage.getItem(LAST_USER_ID_KEY)
      ? Number(localStorage.getItem(LAST_USER_ID_KEY))
      : null

    if (currentUserId && storedLastUserId !== null && currentUserId !== storedLastUserId) {
      localStorage.removeItem('selectedProducts')
      localStorage.removeItem('selectedProductsMedice')
      localStorage.removeItem('@CANNECT:ORDER_CHECKOUT_CART_ID')
      localStorage.removeItem('@CANNECT:CHECKOUT_CART')
      localStorage.removeItem('@CANNECT:ORDER_CHECKOUT_CART')
      localStorage.removeItem('@CANNECT:RECIPE_WARNING')
      localStorage.removeItem('@CANNECT:CREDIT_SELECTION')
      localStorage.removeItem('@CANNECT:CARD_PIX_SELECTION')
      localStorage.removeItem('@CANNECT:CARD_BOLETO_SELECTION')
      localStorage.removeItem('@CANNECT:TWO_CARDS_SELECTION')
      localStorage.removeItem('@CANNECT:SELECTED_CARD_EXTERNAL_ID')
      localStorage.removeItem('@CANNECT:DELIVERY_CEP')
      localStorage.removeItem('@CANNECT:DELIVERY_ESTIMATE')

      setOrderId(null)
      setOrderData({ items: [], recipes: [] })
      setStatusPaid({})
      setIsUpdate(false)
      setCoupon(null)
      setDeliveryCepState('')
      setDeliveryEstimateSnapshot(null)
      cartItemsRef.current = []
      lastPersonIdRef.current = null

      localStorage.setItem(LAST_USER_ID_KEY, String(currentUserId))
    }

    if (currentUserId) {
      const pendingSessionCartId = getSessionCartId()
      if (isSessionCartResolved(pendingSessionCartId)) {
        clearSessionCartId()
      }
      setAnonOrderData({ items: [] })

      if (currentUserId === storedLastUserId || storedLastUserId === null) {
        if (currentUserId !== storedLastUserId) {
          localStorage.setItem(LAST_USER_ID_KEY, String(currentUserId))
        }
      }
      lastUserIdRef.current = currentUserId
    } else if (!currentUserId && lastUserIdRef.current !== null) {
      localStorage.removeItem('selectedProducts')
      localStorage.removeItem('selectedProductsMedice')
      localStorage.removeItem('@CANNECT:ORDER_CHECKOUT_CART_ID')
      localStorage.removeItem('@CANNECT:CHECKOUT_CART')
      localStorage.removeItem('@CANNECT:ORDER_CHECKOUT_CART')
      localStorage.removeItem('@CANNECT:RECIPE_WARNING')
      localStorage.removeItem('@CANNECT:CREDIT_SELECTION')
      localStorage.removeItem('@CANNECT:CARD_PIX_SELECTION')
      localStorage.removeItem('@CANNECT:CARD_BOLETO_SELECTION')
      localStorage.removeItem('@CANNECT:TWO_CARDS_SELECTION')
      localStorage.removeItem('@CANNECT:SELECTED_CARD_EXTERNAL_ID')
      localStorage.removeItem('@CANNECT:DELIVERY_CEP')
      localStorage.removeItem('@CANNECT:DELIVERY_ESTIMATE')
      setDeliveryCepState('')
      setDeliveryEstimateSnapshot(null)

      lastUserIdRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setters de useLocalStorage têm referência instável a cada render; a dependência real do efeito é apenas signedUser?.id
  }, [signedUser?.id])

  useEffect(() => {
    if (signedUser?.id) return
    if (!getSessionCartId()) return
    getAnonymousCart()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedUser?.id])

  useEffect(() => {
    const currentPersonId = orderData?.person?.id
    if (currentPersonId !== lastPersonIdRef.current && lastPersonIdRef.current !== null) {
      setLastOrderPaid({})
      lastPersonIdRef.current = currentPersonId || null
    } else if (currentPersonId && lastPersonIdRef.current === null) {
      lastPersonIdRef.current = currentPersonId
    }
  }, [orderData?.person?.id])

  useEffect(() => {
    try {
      const raw = localStorage.getItem('selectedProducts')
      const parsed = raw ? JSON.parse(raw) : []

      if (!Array.isArray(parsed)) {
        localStorage.removeItem('selectedProducts')
        setOrderData((prevData: any) => {
          cartItemsRef.current = []
          return { ...prevData, items: [] }
        })
        return
      }

      const normalized = parsed
        .map((item: any) => {
          const productId = item?.product_id ?? item?.id ?? (item?.product && item?.product?.id)
          const quantity = Number(item?.quantity ?? item?.product_quantity ?? item?.amount ?? 1)

          if (!productId || Number.isNaN(quantity) || quantity <= 0) return null

          return {
            id: productId,
            product_id: productId,
            quantity,
            product: item?.product && item?.product?.id ? item.product : { id: productId }
          }
        })
        .filter(Boolean)

      if (!normalized.length) {
        localStorage.removeItem('selectedProducts')
      } else {
        localStorage.setItem('selectedProducts', JSON.stringify(normalized))
      }

      setOrderData((prevData: any) => {
        cartItemsRef.current = normalized
        return { ...prevData, items: normalized }
      })
    } catch {
      localStorage.removeItem('selectedProducts')
      setOrderData((prevData: any) => {
        cartItemsRef.current = []
        return { ...prevData, items: [] }
      })
    }
  }, [])

  useEffect(() => {
    const noOrderId = !(orderData?.id || orderId || cartId || stateOrderId)
    const hasLocalItems = (cartItemsRef.current || []).length > 0
    if (!withFilter && noOrderId && hasLocalItems) {
      clearOrderData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withFilter, orderData?.id, orderId, cartId, stateOrderId])

  async function AddToCartByFlavor(items: any[]) {
    const oldCartItems = orderData?.items || []
    const formattedItems = items.map((item) => ({
      id: item?.product_id || item?.id,
      product_id: item?.product_id || item?.id,
      flavor: item?.flavor?.name ?? item?.flavor,
      flavors: item?.flavors,
      // idsToAdd (SelectFlavorModal) e toAdd (FlavorQuantitySelector) mandam `name`/`product_id`
      // puros, não `translated_name` — sem o fallback, o item ia pro carrinho sem nome até o
      // refetch do updateCart trazer o dado completo do backend.
      name: item?.name ?? item?.translated_name,
      quantity: item?.quantity,
      stock: item?.stock,
      product: {
        id: item?.id,
        main_product_id: item?.id
      },
      main_product_id: item?.main_product_id
    }))

    const updatedCartItems = oldCartItems.map((existingItem: any) => {
      const product_id = existingItem?.product_id || existingItem?.id
      const matchingItem = formattedItems.find((item) => product_id === item.id)

      if (matchingItem) {
        return {
          ...existingItem,
          quantity: matchingItem.quantity
        }
      }

      return existingItem
    })

    const newItems = formattedItems.filter(
      (item) => !updatedCartItems.some((existingItem: any) => existingItem.product_id === item.product_id)
    )

    const finalCartItems = [...updatedCartItems, ...newItems]

    await updateCart(finalCartItems)
  }

  const handlePatientIsUser = () => {
    setPatientIsUser(true)
  }

  const clearOrderData = () => {
    setOrderId(null)
    setOrderData({ items: [], recipes: [] })
    setStatusPaid({})
    setIsUpdate(false)
    setDeliveryEstimateSnapshot(null)
    localStorage.removeItem('selectedProducts')
    localStorage.removeItem('selectedProductsMedice')
    localStorage.removeItem('@CANNECT:DELIVERY_ESTIMATE')
  }

  const getLastOrder = async (loading = true) => {
    if (withFilter) {
      await getPublicCart({ cart_id: withFilter }, loading)
    } else {
      await getLastOrders(loading)
    }
  }

  const hasDuplicatedItems = (items: any[]): boolean => {
    const productIdList = items.map((i: any) => i.product_id)
    const uniqueProductIdList = [...new Set([...productIdList])]
    return productIdList.length > uniqueProductIdList.length
  }

  const removeDuplicatedCartItems = (items: any[]) => {
    const uniqueItems: any[] = []
    const itemMap = new Map()

    items.forEach((item: any) => {
      const existingItem = itemMap.get(item.product_id)
      if (existingItem) {
        existingItem.quantity += item.quantity
      } else {
        itemMap.set(item.product_id, { ...item })
      }
    })

    itemMap.forEach((item) => uniqueItems.push(item))
    return uniqueItems
  }

  const getLastOrderPaid = async (userId: number) => {
    if (!userId) {
      setLastOrderPaid({})
      return
    }
    try {
      const { data } = await api.get(`/order_last_paid_b2c/${userId}`)
      if (userId === orderData?.person?.id) {
        setLastOrderPaid(data?.order || {})
      }
    } catch (error) {
      console.error('Erro ao buscar último pedido pago:', error)
      setLastOrderPaid({})
    }
  }

  /** Busca o pedido por ID e atualiza o estado (inclui itens com promotion). Usado após addToCart para exibir preço promocional. */
  const refetchOrderById = async (orderIdToFetch: number | string, loadingState = false) => {
    if (!orderIdToFetch) return
    setLoading(loadingState)
    try {
      const { data } = await api.post(`/order_status/${orderIdToFetch}`)
      if (data?.order) {
        const orderToSet = hasDuplicatedItems(data?.order?.items)
          ? { ...data.order, items: removeDuplicatedCartItems(data.order.items) }
          : data.order
        setOrderData(applyDeliveryEstimateSnapshotToOrder(orderToSet, deliveryEstimateSnapshot))
        cartItemsRef.current = orderToSet?.items || []

        if (data?.order?.coupon_id && data?.order?.coupon) {
          setCoupon(data?.order?.coupon)
        }
      }
      setStatusPaid(data?.payment)
      setPatientIsUser(data?.patientIsUser)

      if (data?.cartModified && data?.cartChanges) {
        setCartChanges(data.cartChanges)
        setShowCartModifiedModal(true)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setValidatedStatus(true)
      setIsUpdate(false)
      setLoading(false)
      setCartLoading(false)
    }
  }

  const getLastOrders = async (loading = true) => {
    const id = cartId || orderId || stateOrderId
    if (!id) return
    await refetchOrderById(id, loading)
  }

  const getPublicCart = async ({ cart_id }: { cart_id?: string; loading?: boolean }, loading = true) => {
    setLoading(loading)
    try {
      const { data } = await api.get(`/order_b2c_public/${cart_id}`)
      if (data?.order) {
        setOrderData(applyDeliveryEstimateSnapshotToOrder(data?.order, deliveryEstimateSnapshot))
        setPatientIsUser(data?.patientIsUser)
      }

      const isPaid = data?.order?.payments?.some((payment: any) => payment.status === 'paid')
      const status = isPaid ? { status: 'paid' } : data?.payment
      setStatusPaid({ ...status })
    } catch (e: any) {
      console.error(e)
    } finally {
      setValidatedStatus(true)
      setLoading(false)
      setCartLoading(false)
    }
  }

  const createOrder = async ({ items }: any) => {
    try {
      const { data } = await api.post('/order_cart', { items })
      setOrderId(data?.order?.id)
      setStateOrderId(data?.order?.id)
      setOrderData(data?.order)
      // Refetch para trazer itens com promotion (preço promocional no carrinho)
      if (data?.order?.id) {
        await refetchOrderById(data.order.id, false)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const clearCart = async () => {
    setOrderData({ items: [], recipes: [] })
    cartItemsRef.current = []
    localStorage.removeItem('selectedProducts')
    await updateCart([])
  }

  const updateCartCoupon = async (coupon: any, items: any, shippingDiscount?: number) => {
    setIsUpdate(true)
    setLoadingCupon(true)
    try {
      const couponPayload = coupon?.name ? { name: coupon.name } : null

      const itemsArray = items || orderData?.items || []

      const formattedItems = itemsArray.map((item: any) => ({
        product_id: item?.product_id || item?.id,
        quantity: item?.quantity || 1
      }))

      const payload: any = {
        items: formattedItems,
        coupon: couponPayload
      }

      if (shippingDiscount !== undefined && shippingDiscount !== null) {
        payload.shippingDiscount = parseFloat(shippingDiscount.toString())
      } else if (orderData?.shippingDiscount) {
        payload.shippingDiscount = parseFloat(orderData.shippingDiscount)
      }
      if (orderData?.items?.[0]?.product?.brand_id) {
        payload.brand_id = orderData.items[0].product.brand_id
      }

      const { data } = await api.put(`/${isPublicUrl}/${orderData?.id}`, payload)

      // Backend reprova o cupom quando o desconto progressivo do carrinho é melhor (comparação por total)
      const rejectedByProgressive = data?.applied === false || data?.winner === 'progressive'

      if (data?.order) {
        setOrderData(data.order)
        setPatientIsUser(data?.patientIsUser)

        if (!rejectedByProgressive && data.order.coupon) {
          setCoupon(data.order.coupon)
          toast.success('Cupom aplicado com sucesso')
        } else {
          setCoupon(null)
          toast.error(data?.message || 'Seu carrinho já possui um desconto melhor (progressivo)')
        }
      }
    } catch (e: any) {
      console.error('Erro ao aplicar cupom:', e)
      const errorMessage = e?.response?.data?.error || e?.response?.data?.message || 'Erro ao aplicar cupom'
      toast.error(errorMessage)
      setCoupon(null)
    } finally {
      setIsUpdate(false)
      setLoadingCupon(false)
    }
  }

  const updateCartRecipes = async (recipeId: any) => {
    try {
      const { data } = await api.put(`/${isPublicUrl}/${orderData?.id}`, { recipes: [{ id: recipeId }] })
      setOrderData(data?.order)
    } catch (e) {
      console.error(e)
    } finally {
      setLoadingCupon(false)
    }
  }

  const updateCart = async (itemsToAdd: any[] = [], idsToRemove: number[] = [], validateDuplicated = true) => {
    setLoadingCupon(true)
    setIsUpdate(true)
    try {
      const currentItems = cartItemsRef.current || []

      const remainingItems = currentItems.filter((item: any) => {
        const comparableId = item?.product?.id ?? item?.product_id ?? item?.id
        return (
          !idsToRemove.includes(item?.id) &&
          !idsToRemove.includes(item?.product_id) &&
          !idsToRemove.includes(comparableId)
        )
      })

      itemsToAdd.forEach((newItem) => {
        const existingItemIndex = remainingItems.findIndex((item: any) => item.product_id === newItem.product_id)
        if (existingItemIndex !== -1) {
          remainingItems[existingItemIndex].quantity = newItem.quantity
        } else {
          remainingItems.push(newItem)
        }
      })

      // Sem pedido ainda (carrinho vazio): cria o pedido, igual ao addToCart. Sem isso, o
      // updateCart não fazia nada nesse caso e o item só aparecia após um refresh da página.
      const existingOrderId = orderData?.id || orderId || cartId || stateOrderId
      if (!existingOrderId) {
        await createOrder({ items: remainingItems })
        validateCartState(idsToRemove)
        return
      }

      const { data } = await api.put(`/${isPublicUrl}/${existingOrderId}`, { items: remainingItems })
      const validItems = (data?.order?.items || []).filter((item: any) => {
        const comparableId = item?.product?.id ?? item?.product_id ?? item?.id
        return (
          !idsToRemove.includes(item?.id) &&
          !idsToRemove.includes(item?.product_id) &&
          !idsToRemove.includes(comparableId)
        )
      })
      setOrderData((prevOrderData: any) => {
        cartItemsRef.current = validItems
        return { ...data.order, items: validItems }
      })

      const normalized = validItems.map((i: any) => ({
        id: i?.product_id ?? i?.id ?? i?.product?.id,
        product_id: i?.product_id ?? i?.id ?? i?.product?.id,
        quantity: i?.quantity ?? i?.product_quantity ?? i?.amount ?? 1,
        product: i?.product?.id ? i.product : { id: i?.product_id ?? i?.id }
      }))
      localStorage.setItem('selectedProducts', JSON.stringify(normalized))

      // Refetch para trazer os itens completos (nome, sabor, preço promocional) — o PUT acima
      // não retorna o mesmo shape enriquecido que /order_status, igual ao addToCart.
      await refetchOrderById(existingOrderId, false)

      validateCartState(idsToRemove)
    } catch (e) {
      console.error(e)
      toast.error('Erro ao atualizar carrinho')
    } finally {
      setIsUpdate(false)
      setLoadingCupon(false)
    }
  }

  const incrementOrDecrementCart = async (id: number, quantity: number) => {
    setCartLoading(true)
    try {
      const newValues = orderData?.items?.map((item: any) => {
        return item?.product_id === id ? { ...item, quantity } : item
      })
      setOrderData({ ...orderData, items: newValues })
      cartItemsRef.current = newValues
      await updateCart(newValues)
    } catch (error) {
      console.error('Erro ao atualizar quantidade:', error)
    } finally {
      setCartLoading(false)
    }
  }

  const addToAnonCart = async (id: number, quantity: number) => {
    setCartLoading(true)
    setLoadingCupon(true)

    try {
      let sessionCartId = getSessionCartId()
      if (!sessionCartId) {
        sessionCartId = await initSessionCartId()
      }

      await api.post(`/anonymous-cart/${sessionCartId}/items`, { product_id: id, quantity })
      toast.success('Produto adicionado ao carrinho')
      await getAnonymousCart()
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        (error?.response?.status === 422
          ? 'Este produto requer autenticação para ser adicionado ao carrinho'
          : 'Erro ao adicionar produto ao carrinho')
      toast.error(message)
      console.error('Erro ao adicionar produto ao carrinho anônimo:', error)
    } finally {
      setLoadingCupon(false)
      setCartLoading(false)
    }
  }

  const getAnonymousCart = async () => {
    const sessionCartId = getSessionCartId()
    if (!sessionCartId) {
      setAnonOrderData({ items: [] })
      return
    }

    try {
      const { data } = await api.get(`/anonymous-cart/${sessionCartId}`)
      setAnonOrderData(data?.data || { items: [] })
    } catch (error: any) {
      if (error?.response?.status === 404) {
        clearSessionCartId()
      }
      setAnonOrderData({ items: [] })
    }
  }

  const updateAnonItem = async (itemId: number, quantity: number) => {
    const sessionCartId = getSessionCartId()
    if (!sessionCartId) return

    try {
      await api.put(`/anonymous-cart/${sessionCartId}/items/${itemId}`, { quantity })
      await getAnonymousCart()
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Erro ao atualizar carrinho')
    }
  }

  const removeAnonItem = async (itemId: number) => {
    const sessionCartId = getSessionCartId()
    if (!sessionCartId) return

    try {
      await api.delete(`/anonymous-cart/${sessionCartId}/items/${itemId}`)
      await getAnonymousCart()
      toast.success('Item removido do carrinho')
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Erro ao remover item do carrinho')
    }
  }

  const validateAnonDeliveryCep = async (opts?: { silent?: boolean }): Promise<boolean> => {
    const silent = opts?.silent === true

    const cepDigits = (deliveryCep || '').replace(/\D/g, '')
    if (cepDigits.length !== 8) {
      if (!silent) toast.error('Informe um CEP válido')
      return false
    }

    const sessionCartId = getSessionCartId()
    if (!sessionCartId) {
      if (!silent) toast.error('Carrinho não encontrado')
      return false
    }

    setValidatingDeliveryCep(true)
    try {
      const { data } = await api.post(`/anonymous-cart/${sessionCartId}/shipping`, { cep: deliveryCep })
      setAnonShippingData(data?.data || null)
      if (!silent) toast.success('CEP validado com sucesso')
      return true
    } catch (error: any) {
      if (!silent) toast.error(error?.response?.data?.message || 'Erro ao validar CEP')
      return false
    } finally {
      setValidatingDeliveryCep(false)
    }
  }

  useEffect(() => {
    const signature = getCartItemsSignature(anonOrderData?.items || [])
    const changed = anonItemsSignatureRef.current !== '' && signature !== anonItemsSignatureRef.current
    anonItemsSignatureRef.current = signature

    if (!changed || signedUser?.id) return

    const cepDigits = (deliveryCep || '').replace(/\D/g, '')
    if (cepDigits.length !== 8) {
      setAnonShippingData(null)
      return
    }

    // Carrinho mudou depois do CEP validado: a estimativa antiga não vale mais.
    // Revalida em silêncio para não repetir toasts a cada +/- de item.
    setAnonShippingData(null)
    validateAnonDeliveryCep({ silent: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- validateAnonDeliveryCep/deliveryCep/signedUser não são estáveis; a dependência real é a composição de itens
  }, [anonOrderData?.items])

  async function addToCart(
    id: number,
    quantity?: number,
    class_id?: number,
    brand_id?: number,
    type?: string,
    is_cannabis?: boolean
  ) {
    const isAuthenticated = !!signedUser?.id

    if (!isAuthenticated) {
      if (is_cannabis) {
        history.push({ pathname: '/login', state: { from: { pathname: window.location.pathname } } })
        return
      }

      await addToAnonCart(id, quantity || 1)
      return
    }

    setCartLoading(true)
    setLoadingCupon(true)

    try {
      const existingItem = orderData?.items?.find((item: any) => item.product_id === id)
      const formatValues = existingItem
        ? orderData?.items.map((itemCart: any) => {
            if (itemCart.product_id === id) {
              return {
                product_id: itemCart?.product_id || itemCart?.product?.id,
                quantity: itemCart.quantity + (quantity || 1),
                brand_id: itemCart?.product?.brand_id,
                class_id: itemCart?.product?.class_id,
                type: itemCart?.product?.type
              }
            }
            return itemCart
          })
        : [...orderData.items, { product_id: id, quantity: quantity || 1, brand_id, class_id, type }]

      const existingOrderId = orderId || cartId || stateOrderId
      if (!existingOrderId) {
        await createOrder({ items: formatValues })
        toast.success('Produto adicionado ao carrinho')
      } else {
        try {
          const { data } = await api.put(`/order_cart/${existingOrderId}`, { items: formatValues })
          setOrderData(data?.order)
          // Refetch para trazer itens com promotion (preço promocional no carrinho)
          await refetchOrderById(existingOrderId, false)
          toast.success('Produto adicionado ao carrinho')
        } catch (error: any) {
          if (error?.response?.status === 422) {
            toast.error('Este produto está indisponível no momento.')
          } else {
            setOrderId(null)
            setCartId(null)
            setStateOrderId(null)
            toast.error('Erro ao adicionar produto no pedido')
          }
        }
      }
    } catch (error) {
      console.error('Erro ao adicionar produto ao carrinho:', error)
      toast.error('Erro ao adicionar produto ao carrinho')
    } finally {
      setLoadingCupon(false)
      setCartLoading(false)
    }
  }

  async function addToCartFlavor(idRemove: number, id: number, quantity?: number) {
    setCartLoading(true)
    setIsUpdate(true)
    const normalizedIdRemove = Number(idRemove)
    const normalizedNewId = Number(id)

    const baseItems =
      orderData?.items
        ?.filter((item: any) => {
          const comparableId = Number(item?.product?.id ?? item?.product_id ?? item?.id)
          return comparableId !== normalizedIdRemove
        })
        .map((item: any) => ({
          product_id: Number(item?.product_id ?? item?.product?.id ?? item?.id),
          quantity: Number(item?.quantity ?? item?.product_quantity ?? 1)
        })) || []

    const itemsMap = new Map<number, { product_id: number; quantity: number }>()
    baseItems.forEach((item: any) => {
      if (!item.product_id) return
      itemsMap.set(item.product_id, { product_id: item.product_id, quantity: item.quantity })
    })

    const existingNewItem = itemsMap.get(normalizedNewId)
    itemsMap.set(normalizedNewId, { product_id: normalizedNewId, quantity: quantity || existingNewItem?.quantity || 1 })

    const formatValues = Array.from(itemsMap.values())

    try {
      const { data } = await api.put(`/order_cart/${orderId}`, { items: formatValues })
      setOrderData(data?.order)
      toast.success('Produto adicionado ao carrinho')
    } catch (error) {
      toast.error('Erro ao adicionar produto no carrinho')
    } finally {
      setIsUpdate(false)
      setCartLoading(false)
    }
  }

  const removeCart = async (idsToRemove: number | number[]) => {
    setCartLoading(true)

    try {
      const idsArray = Array.isArray(idsToRemove) ? idsToRemove : [idsToRemove]
      const currentItems = orderData?.items || []
      const remainingItems = currentItems.filter((item: any) => {
        const comparableId = item?.product?.id ?? item?.product_id ?? item?.id
        return (
          !idsArray.includes(item?.id) &&
          !idsArray.includes(item?.product_id) &&
          !idsArray.includes(comparableId) &&
          (item?.quantity ?? item?.product_quantity ?? 0) > 0
        )
      })

      setOrderData((prevOrderData: any) => ({ ...prevOrderData, items: remainingItems }))

      cartItemsRef.current = remainingItems

      if (remainingItems.length === 0) {
        setOrderData({ items: [], recipes: [] })
      }

      try {
        const storedProducts = JSON.parse(localStorage.getItem('selectedProducts') || '[]')
        const updatedStoredProducts = (Array.isArray(storedProducts) ? storedProducts : []).filter(
          (storedProduct: any) => {
            const comparableId = storedProduct?.product?.id ?? storedProduct?.product_id ?? storedProduct?.id
            const qty = storedProduct?.quantity ?? storedProduct?.product_quantity ?? 0
            return (
              !idsArray.includes(storedProduct?.id) &&
              !idsArray.includes(storedProduct?.product_id) &&
              !idsArray.includes(comparableId) &&
              qty > 0
            )
          }
        )
        localStorage.setItem('selectedProducts', JSON.stringify(updatedStoredProducts))
      } catch {
        localStorage.removeItem('selectedProducts')
      }
      await updateCart([], idsArray)
    } catch (error) {
      console.error('Erro ao remover item do carrinho:', error)
    } finally {
      setCartLoading(false)
    }
  }

  const validateCartState = (idsToRemove: number[]) => {
    let remainingItems = cartItemsRef.current || []
    const invalidItemsInOrderData = remainingItems.filter((item: any) => {
      const comparableId = item?.product?.id ?? item?.product_id ?? item?.id
      return (
        idsToRemove.includes(item?.id) || idsToRemove.includes(item?.product_id) || idsToRemove.includes(comparableId)
      )
    })
    if (invalidItemsInOrderData.length > 0) {
      remainingItems = remainingItems.filter((item: any) => {
        const comparableId = item?.product?.id ?? item?.product_id ?? item?.id
        return (
          !idsToRemove.includes(item?.id) &&
          !idsToRemove.includes(item?.product_id) &&
          !idsToRemove.includes(comparableId)
        )
      })
      setOrderData((prevOrderData: any) => ({
        ...prevOrderData,
        items: remainingItems
      }))
      cartItemsRef.current = remainingItems
    }

    const storedRaw = localStorage.getItem('selectedProducts')
    let storedProducts: any[] = []
    try {
      storedProducts = storedRaw ? JSON.parse(storedRaw) : []
      if (!Array.isArray(storedProducts)) storedProducts = []
    } catch {
      storedProducts = []
    }
    const invalidItemsInLocalStorage = storedProducts.filter((storedProduct: any) => {
      const comparableId = storedProduct?.product?.id ?? storedProduct?.product_id ?? storedProduct?.id
      return (
        idsToRemove.includes(storedProduct?.id) ||
        idsToRemove.includes(storedProduct?.product_id) ||
        idsToRemove.includes(comparableId)
      )
    })
    if (invalidItemsInLocalStorage.length > 0) {
      const updatedStoredProducts = storedProducts.filter((storedProduct: any) => {
        const comparableId = storedProduct?.product?.id ?? storedProduct?.product_id ?? storedProduct?.id
        return (
          !idsToRemove.includes(storedProduct?.id) &&
          !idsToRemove.includes(storedProduct?.product_id) &&
          !idsToRemove.includes(comparableId)
        )
      })
      localStorage.setItem('selectedProducts', JSON.stringify(updatedStoredProducts))
    }
  }

  const removeCoupon = async () => {
    setIsUpdate(true)
    setLoadingCupon(true)
    try {
      const items = orderData?.items?.map((item: any) => {
        return {
          product_id: item?.product_id,
          quantity: item?.quantity
        }
      })

      const payload = {
        items,
        coupon: null, // Explicitamente remove o cupom
        discount: 0,
        discount_resume_changed: false,
        freight: parseFloat(orderData?.shipping) || 0,
        resume_changed: false,
        prices_changed: false,
        brand_id: orderData?.items[0]?.product?.brand_id,
        shippingDiscount: 0
      }

      const { data } = await api.put(`/order_b2c/${orderData?.id}`, payload)

      setOrderData(data?.order)
      setCoupon(null)
      toast.success('Cupom removido com sucesso')
    } catch (e) {
      toast.error('Erro ao remover cupom')
      console.error('[removeCoupon] Erro:', e)
    } finally {
      setIsUpdate(false)
      setLoadingCupon(false)
      getLastOrder()
    }
  }

  useEffect(() => {
    cartItemsRef.current = orderData?.items || []
  }, [orderData?.items])

  useEffect(() => {
    if (!orderData?.id || !orderData?.cep) return
    if (seededCepFromOrderRef.current === orderData.id) return

    seededCepFromOrderRef.current = orderData.id
    if (!deliveryCep) {
      setDeliveryCep(orderData.cep)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed único por pedido; setDeliveryCep não é estável
  }, [deliveryCep, orderData?.cep, orderData?.id])

  useEffect(() => {
    if (!orderData?.id || !hasOrderDeliveryEstimate(orderData)) return

    const nextSnapshot = getDeliveryEstimateSnapshotFromOrder(orderData, deliveryCep || orderData?.cep || '')
    const currentSnapshot = deliveryEstimateSnapshot ? JSON.stringify(deliveryEstimateSnapshot) : null
    const nextSnapshotString = nextSnapshot ? JSON.stringify(nextSnapshot) : null

    if (nextSnapshotString && currentSnapshot !== nextSnapshotString) {
      setDeliveryEstimateSnapshot(nextSnapshot)
    }
  }, [
    orderData?.id,
    orderData?.cep,
    orderData?.estimated_delivery_business_days,
    orderData?.estimated_delivery_range_start,
    orderData?.estimated_delivery_range_end,
    orderData?.estimated_delivery_incomplete,
    deliveryCep,
    deliveryEstimateSnapshot,
    setDeliveryEstimateSnapshot
  ])

  useEffect(() => {
    if (!orderData?.id || !deliveryEstimateSnapshot?.orderId) return

    const hydratedOrder = applyDeliveryEstimateSnapshotToOrder(orderData, deliveryEstimateSnapshot)
    const hasChanged =
      hydratedOrder?.estimated_delivery_business_days !== orderData?.estimated_delivery_business_days ||
      hydratedOrder?.estimated_delivery_range_start !== orderData?.estimated_delivery_range_start ||
      hydratedOrder?.estimated_delivery_range_end !== orderData?.estimated_delivery_range_end ||
      hydratedOrder?.estimated_delivery_incomplete !== orderData?.estimated_delivery_incomplete

    if (hasChanged) {
      setOrderData(hydratedOrder)
    }
  }, [orderData, deliveryEstimateSnapshot])

  useEffect(() => {
    if (withFilter) {
      return
    }

    // Sem cart_id a rota é a autenticada (order_cart); sem sessão ela só devolve 401 e o auto-sync insistiria nela.
    if (!signedUser?.id) return

    const normalizedDeliveryCep = normalizeCep(deliveryCep)
    const orderItemsSignature = getCartItemsSignature(orderData?.items || [])
    const syncKey = `${orderData?.id || 'no-order'}:${normalizedDeliveryCep}:${orderItemsSignature}`
    const orderCep = normalizeCep(orderData?.cep || '')
    const orderHasEstimate = hasOrderDeliveryEstimate(orderData)

    if (!orderData?.id || !orderItemsSignature || normalizedDeliveryCep.replace(/\D/g, '').length !== 8) {
      return
    }

    if (finalizedOrderIdRef.current === orderData.id) return

    if (orderHasEstimate && orderCep === normalizedDeliveryCep) {
      lastAutoEstimatedKeyRef.current = syncKey
      return
    }

    if (validatingDeliveryCep || lastAutoEstimatedKeyRef.current === syncKey) {
      return
    }

    lastAutoEstimatedKeyRef.current = syncKey
    syncDeliveryEstimate({ silent: true }).then((success) => {
      if (!success) {
        lastAutoEstimatedKeyRef.current = ''
      }
    })
  }, [
    orderData?.id,
    orderData?.cep,
    orderData?.items,
    orderData?.estimated_delivery_business_days,
    orderData?.estimated_delivery_range_start,
    orderData?.estimated_delivery_range_end,
    deliveryCep,
    validatingDeliveryCep,
    withFilter,
    signedUser?.id
  ])

  return (
    <PrepareCartContext.Provider
      value={{
        AddToCartByFlavor,
        clearOrderData,
        getLastOrder,
        lastOrderCreated: orderData,
        addToCart,
        incrementOrDecrementCart,
        clearCart,
        removeCart,
        newLastOrder: setOrderData,
        getPublicCart,
        loading,
        setLoading,
        recipesDocuments,
        setRecipeDocuments,
        updateCartCoupon,
        loadingCupon,
        isUpdate,
        updateCart,
        patientIsUser,
        handlePatientIsUser,
        updateCartRecipes,
        statusPaid,
        addToCartFlavor,
        setOrderId,
        setPaymentRetry,
        paymentRetry,
        setCartId,
        cartId,
        setOrderData,
        cartLoading,
        coupon,
        removeCoupon,
        lastOrderPaid,
        getLastOrderPaid,
        showCartModifiedModal,
        setShowCartModifiedModal,
        cartChanges,
        deliveryCep,
        setDeliveryCep,
        validateDeliveryCep,
        validatingDeliveryCep,
        anonOrderData,
        getAnonymousCart,
        updateAnonItem,
        removeAnonItem,
        anonShippingData,
        validateAnonDeliveryCep
      }}>
      {children}
    </PrepareCartContext.Provider>
  )
}

function usePrepareCart() {
  return useContext(PrepareCartContext)
}

export { PrepareCartProvider, usePrepareCart }
